/**
 * Admin API module.
 *
 * Thin, typed wrappers over the internal admin routes (same Go API as the
 * customer dashboard). Each function returns the DTO type from
 * ./admin-types.ts and throws `ApiError` on failure. Everything is
 * Bearer-authenticated via the shared API client (src/api/client.ts), which
 * pulls the Clerk session token at request time.
 *
 * All admin routes enforce the operator role SERVER-SIDE (admin = read/write,
 * support = read-only). These wrappers never fabricate authorization.
 */
import { apiRequest } from './client';
import type {
  AdminAccountDetailResponse,
  AdminAccountStatusRequest,
  AdminAccountStatusResponse,
  AdminActionListParams,
  AdminActionListResponse,
  AdminApiKeyListResponse,
  AdminApiKeyRevokeRequest,
  AdminApiKeyRevokeResponse,
  AdminLookupResponse,
  AdminMeResponse,
  AdminPlanGrantRequest,
  AdminPlanGrantResponse,
  AdminUsageResponse,
  BillingAuditResponse,
  EntitlementAuditResponse,
  UsageGranularity,
} from './admin-types';

const ADMIN = '/v1/admin';

/** Default page size for audit + action listings (contract default). */
const DEFAULT_LIMIT = 50;

/* ----- Operator identity ----- */

/** Who am I? Returns the caller's operator role (admin|support). */
export function getAdminMe(signal?: AbortSignal): Promise<AdminMeResponse> {
  return apiRequest<AdminMeResponse>(`${ADMIN}/me`, { signal }).then((r) => r.data);
}

/* ----- Lookup ----- */

/** Cross-entity lookup by free-text query (email, account id, clerk user id…). */
export function adminLookup(query: string, signal?: AbortSignal): Promise<AdminLookupResponse> {
  return apiRequest<AdminLookupResponse>(`${ADMIN}/lookup`, {
    query: { q: query },
    signal,
  }).then((r) => r.data);
}

/* ----- Account detail + audits ----- */

export function getAdminAccount(
  accountID: string,
  signal?: AbortSignal,
): Promise<AdminAccountDetailResponse> {
  return apiRequest<AdminAccountDetailResponse>(`${ADMIN}/accounts/${accountID}`, { signal }).then(
    (r) => r.data,
  );
}

export function getEntitlementAudit(
  accountID: string,
  limit: number = DEFAULT_LIMIT,
  signal?: AbortSignal,
): Promise<EntitlementAuditResponse> {
  return apiRequest<EntitlementAuditResponse>(
    `${ADMIN}/accounts/${accountID}/entitlement-audit`,
    { query: { limit }, signal },
  ).then((r) => r.data);
}

export function getBillingAudit(
  accountID: string,
  limit: number = DEFAULT_LIMIT,
  signal?: AbortSignal,
): Promise<BillingAuditResponse> {
  return apiRequest<BillingAuditResponse>(`${ADMIN}/accounts/${accountID}/billing-audit`, {
    query: { limit },
    signal,
  }).then((r) => r.data);
}

/* ----- Usage + API keys ----- */

export function getAdminUsage(
  accountID: string,
  params?: { granularity?: UsageGranularity; days?: number },
  signal?: AbortSignal,
): Promise<AdminUsageResponse> {
  return apiRequest<AdminUsageResponse>(`${ADMIN}/accounts/${accountID}/usage`, {
    query: params,
    signal,
  }).then((r) => r.data);
}

export function getAdminApiKeys(
  accountID: string,
  signal?: AbortSignal,
): Promise<AdminApiKeyListResponse> {
  return apiRequest<AdminApiKeyListResponse>(`${ADMIN}/accounts/${accountID}/api-keys`, {
    signal,
  }).then((r) => r.data);
}

/* ----- Mutations (admin role only, enforced server-side) ----- */

export function grantAdminPlan(
  accountID: string,
  body: AdminPlanGrantRequest,
  idempotencyKey?: string,
  signal?: AbortSignal,
): Promise<AdminPlanGrantResponse> {
  return apiRequest<AdminPlanGrantResponse>(`${ADMIN}/accounts/${accountID}/plan`, {
    method: 'POST',
    body,
    idempotencyKey,
    signal,
  }).then((r) => r.data);
}

export function revokeAdminApiKey(
  accountID: string,
  keyID: string,
  body: AdminApiKeyRevokeRequest,
  signal?: AbortSignal,
): Promise<AdminApiKeyRevokeResponse> {
  return apiRequest<AdminApiKeyRevokeResponse>(
    `${ADMIN}/accounts/${accountID}/api-keys/${keyID}/revoke`,
    { method: 'POST', body, signal },
  ).then((r) => r.data);
}

export function setAdminAccountStatus(
  accountID: string,
  body: AdminAccountStatusRequest,
  signal?: AbortSignal,
): Promise<AdminAccountStatusResponse> {
  return apiRequest<AdminAccountStatusResponse>(`${ADMIN}/accounts/${accountID}/status`, {
    method: 'POST',
    body,
    signal,
  }).then((r) => r.data);
}

/* ----- Operator action log ----- */

export function getAdminActions(
  params?: AdminActionListParams,
  signal?: AbortSignal,
): Promise<AdminActionListResponse> {
  return apiRequest<AdminActionListResponse>(`${ADMIN}/actions`, {
    query: { account_id: params?.account_id, limit: params?.limit ?? DEFAULT_LIMIT },
    signal,
  }).then((r) => r.data);
}
